import { backendClient } from '../lib/api/backendClient';

export type PreferenceEntityType = 'composer' | 'work' | 'artist' | 'recording';

export type UserEntityPreference = {
  entityType: PreferenceEntityType;
  entityId: string;
  weight: number;
  createdAt: string;
  updatedAt: string;
};

type ListPreferencesResponse = {
  preferences?: Array<Record<string, unknown>>;
};

type UpsertPreferenceResponse = {
  preference?: Record<string, unknown>;
};

const LIST_CACHE_TTL_MS = 1000 * 30; // 30 seconds
let cachedList: { data: UserEntityPreference[]; expiresAt: number } | null = null;
let pendingList: Promise<UserEntityPreference[]> | null = null;

const parsePreference = (value: Record<string, unknown>): UserEntityPreference | null => {
  const entityType =
    typeof value.entityType === 'string' ? (value.entityType.trim().toLowerCase() as PreferenceEntityType) : '';
  if (!entityType || !['composer', 'work', 'artist', 'recording'].includes(entityType)) {
    return null;
  }
  const entityId = typeof value.entityId === 'string' ? value.entityId.trim() : '';
  const weightRaw = typeof value.weight === 'number' ? value.weight : Number(value.weight ?? NaN);
  if (!entityId || !Number.isFinite(weightRaw)) {
    return null;
  }
  const createdAt = typeof value.createdAt === 'string' ? value.createdAt : '';
  const updatedAt = typeof value.updatedAt === 'string' ? value.updatedAt : '';

  return {
    entityType,
    entityId,
    weight: weightRaw,
    createdAt,
    updatedAt
  };
};

export const preferencesService = {
  async list(): Promise<UserEntityPreference[]> {
    const now = Date.now();
    if (cachedList && cachedList.expiresAt > now) {
      return [...cachedList.data];
    }
    if (pendingList) {
      return pendingList.then((result) => [...result]);
    }

    pendingList = backendClient
      .get<ListPreferencesResponse>('/preferences')
      .then(({ data }) => {
        const entries = Array.isArray(data?.preferences) ? data.preferences : [];
        const normalized: UserEntityPreference[] = [];
        for (const entry of entries) {
          if (!entry || typeof entry !== 'object') continue;
          const parsed = parsePreference(entry as Record<string, unknown>);
          if (parsed) {
            normalized.push(parsed);
          }
        }
        cachedList = { data: normalized, expiresAt: Date.now() + LIST_CACHE_TTL_MS };
        return normalized;
      })
      .finally(() => {
        pendingList = null;
      });

    return pendingList;
  },

  async upsert(input: { entityType: PreferenceEntityType; entityId: string; weight: number }): Promise<UserEntityPreference | null> {
    const payload = {
      entityType: input.entityType,
      entityId: input.entityId,
      weight: input.weight
    };
    const { data } = await backendClient.post<UpsertPreferenceResponse>('/preferences', payload);
    cachedList = null;
    if (data?.preference && typeof data.preference === 'object') {
      return parsePreference(data.preference as Record<string, unknown>);
    }
    return null;
  },

  async remove(entityType: PreferenceEntityType, entityId: string): Promise<void> {
    await backendClient.delete(`/preferences/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`);
    cachedList = null;
  }
};

export type PreferencesService = typeof preferencesService;
